import { PluginIdentifier, PluginIdentifierType } from "./plugin-identifier";

/**
 * Toggle-only plugin definitions. A dependency-free leaf module (like plugin-identifier.ts), so
 * consumers and their specs can use the real definitions without pulling in PluginConfigService.
 */

export const TRANSACTION_PLUGIN_DISABLED_KEY = "gloss-transaction-plugin-disabled";
export const MONTHLY_SUMMARY_PLUGIN_DISABLED_KEY = "gloss-monthly-summary-plugin-disabled";

export const AUTO_BUDGET_PLUGIN_ID = PluginIdentifier.AUTO_BUDGET;
export const AUTO_BUDGET_PLUGIN_DISABLED_KEY = "gloss-auto-budget-plugin-disabled";

/**
 * Plugins with no vault-backed config — just a localStorage on/off switch, enabled unless the
 * user has explicitly turned them off. Single source of truth: add an entry here (plus its own
 * PluginIdentifier + disabled-key constant above) to onboard another one — every consumer
 * (plugin store list, details dialog, feature call sites) derives from this map. Keyed by
 * PluginIdentifierType, so a typo'd or unregistered id doesn't compile (#4813).
 */
export const TOGGLE_ONLY_PLUGIN_DISABLED_KEYS = {
  [PluginIdentifier.TRANSACTION]: TRANSACTION_PLUGIN_DISABLED_KEY,
  [PluginIdentifier.MONTHLY_SUMMARY]: MONTHLY_SUMMARY_PLUGIN_DISABLED_KEY,
  [PluginIdentifier.AUTO_BUDGET]: AUTO_BUDGET_PLUGIN_DISABLED_KEY,
} as const satisfies Partial<Record<PluginIdentifierType, string>>;

export type ToggleOnlyPluginId = keyof typeof TOGGLE_ONLY_PLUGIN_DISABLED_KEYS;

export const TOGGLE_ONLY_PLUGIN_IDS = Object.keys(
  TOGGLE_ONLY_PLUGIN_DISABLED_KEYS,
) as readonly ToggleOnlyPluginId[];

/** Narrows an arbitrary plugin id; use this, not TOGGLE_ONLY_PLUGIN_IDS.includes(). */
export function isToggleOnlyPluginId(id: string | undefined): id is ToggleOnlyPluginId {
  return id != null && Object.prototype.hasOwnProperty.call(TOGGLE_ONLY_PLUGIN_DISABLED_KEYS, id);
}
